<template>
  <parent v-if="!fetchingExternalAccounts && !fetchingBuckets">
    <div class="dashboardWrapper">
      <div>
        <header-dashboard
          ref="headerDashboardRef"
          :items="buckets"
          multi-select-title="Buckets"
          :selected-account="selectedAccount"
          :external-accounts="externalAccounts"
          :is-any-request-loading="isAnyRequestLoading"
          @applyFilters="applyFilters"
          @fetchScrolledItems="setDefaultBucket"
          @changeSelectedAccount="changeSelectedAccount"
          @changeFilter="changeFilter"
        />
        <div>
          <pop-up
            v-if="isDisabled"
            class="position-absolute custom-position"
            title="Create External Account"
            button-text="Go to External Accounts"
            :show-cancel="false"
            :go-back="false"
            @add="$router.push({ name: 'external-accounts' })"
          >
            Before viewing storage analytics, make sure you have initalized storage.
          </pop-up>
          <div>
            <b-tabs class="mt-3">
              <b-tab
                v-for="tab in tabs"
                :key="tab.id"
                :title="tab.title"
                :disabled="tab.disabled"
                :active="currentRouteName === tab.routeName"
                @click="changeView(tab)"
              />
            </b-tabs>
            <transition
              name="fade"
              mode="out-in"
            >
              <router-view
                :key="$route.path"
                class="mt-1"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </parent>
  <loading-page
    v-else
    full
  />
</template>

<script>

import HeaderDashboard from '@/components/analytics/header/HeaderDashboard.vue'
import PopUp from '@/components/popup/Pop-up.vue'

export default {
  name: 'StorageAnalytics',
  components: {
    HeaderDashboard,
    PopUp,

  },
  data() {
    return {
      selectedIds: [],
      filterName: '24-h',
      aggregation24Hr: '30-m',
      currentTimestamp: Math.floor(Date.now() / 1000),
      defaultBucketName: null,
    }
  },
  computed: {
    tabs() {
      return [
        {
          id: 1,
          title: 'Requests',
          routeName: 'storage-requests-analytics',
          actionType: 'fetchRequestStats',
          disabled: this.isDisabled,
          iconName: 'Checkmark',
        },
        {
          id: 2,
          title: 'Traffic',
          routeName: 'storage-traffic-analytics',
          actionType: 'fetchTrafficStats',
          disabled: this.isDisabled,
          iconName: '',
        },
        {
          id: 3,
          title: 'Used Space',
          routeName: 'used-space-analytics',
          actionType: 'fetchUsedSpaceStats',
          disabled: this.isDisabled,
          iconName: '',
        },
        {
          id: 4,
          title: 'Objects Count',
          routeName: 'objects-count-analytics',
          actionType: 'fetchObjectsCount',
          disabled: this.isDisabled,
          iconName: '',
        },
      ]
    },
    currentRouteName() {
      return this.$route.name
    },
    currentActionType() {
      return this.tabs.find(tab => tab.routeName === this.currentRouteName)?.actionType
    },
    isDisabled() {
      return this.buckets.length === 0
    },
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    isAnyRequestLoading() {
      return this.fetchingExternalAccounts || this.fetchingBuckets
    },
    fetchingExternalAccounts() {
      return this.$store.state.externalAccount.fetchingExternalAccounts
    },
    fetchingBuckets() {
      return this.$store.state.accountBuckets.fetchingBuckets
    },
    buckets: {
      get() {
        return this.$store.state.accountBuckets.buckets
      },
      set(account) {
        this.$store.commit('accountBuckets/UPDATE_BUCKETS', [{ ...account }])
      },
    },
    selectedAccount: {
      get() {
        return this.$store.state.externalAccount.externalAccount || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    accountId: {
      get() {
        return this.$store.state.externalAccount.accountId
      },
      set(val) {
        this.$store.commit('externalAccount/SET_ACCOUNT_ID', val)
      },
    },
  },
  created() {
    this.accountId = 0
    this.getAccounts()
  },
  methods: {
    changeView(tab) {
      if (tab.routeName === this.currentRouteName) {
        return
      }
      this.$router.push({ name: tab.routeName })
        .then(() => {
          const query = this.generateQuery(this.filterName, this.$refs.headerDashboardRef.selectedIds)
          this.applyFilters(query)
        })
    },
    applyFilters(data) {
      if (data.selectedIds.length === 0) {
        this.infoToast('There are no buckets selected!')
      } else {
        const query = this.generateQuery(data.filterName, data.selectedIds)
        this.fetchData(query)
      }
    },
    changeFilter(filter) {
      this.filterName = filter
    },
    setDefaultBucket(query) {
      if (!query.initialSelectionDone) {
        query.selectedOptions.push(this.defaultBucketName)
        query.initialSelectionDone = true
      }
    },
    fetchData(query) {
      this.selectedIds = query?.selectedIds?.length > 0 ? query?.selectedIds : this.defaultBucketName
      const aggregation = this.getAggregation(this.filterName)

      const timeInterval = this.getTimeInterval(this.filterName)

      const requestQuery = {
        accountId: this.accountId,
        body: {
          from: this.currentTimestamp - timeInterval,
          to: this.currentTimestamp - 4,
          aggregation,
          buckets: query.selectedIds,
        },
      }
      try {
        this.$store.dispatch(`storageAnalytics/${this.currentActionType}`, requestQuery)
      } catch (error) {
        const errorMessage = error.response?.data?.errors[0]
         || `Something went wrong while fetching ${this.currentRouteName.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}.`
        this.errorToast(errorMessage)
      }
    },
    getAggregation(option) {
      const aggregation = {
        '4-h': '5-m',
        '12-h': '10-m',
        '24-h': '30-m',
        '48-h': '1-h',
        '72-h': '2-h',
        '4-d': '3-h',
        '30-d': '1-d',
      }
      return aggregation[option]
    },
    getTimeInterval(option) {
      const intervals = {
        '4-h': 4 * 60 * 60,
        '12-h': 12 * 60 * 60,
        '24-h': 24 * 60 * 60,
        '48-h': 2 * 24 * 60 * 60,
        '72-h': 3 * 24 * 60 * 60,
        '4-d': 4 * 24 * 60 * 60,
        '30-d': 30 * 24 * 60 * 60,
      }
      return intervals[option]
    },
    generateQuery(filterName, selectedIds) {
      return {
        filterName,
        selectedIds,
      }
    },
    getAccounts() {
      this.$store
        .dispatch('externalAccount/fetchAccounts')
        .then(() => {
          if (this.externalAccounts.length > 0) {
            const account = this.externalAccounts[0]
            this.changeSelectedAccount(account)
          }
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
    },
    getBuckets() {
      this.$store
        .dispatch('accountBuckets/fetchBuckets', this.accountId)
        .then(() => {
          this.defaultBucketName = this.buckets[0].name
          this.clearAndFetchAnalytics()
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
            this.$store.commit('buckets/SET_SHOW_INTRO', true)
            return
          }
          if (status === 426 || status === 428) {
            this.$store.commit('accountBuckets/SET_SERVICE_UNAVAILABLE', status === 428)
            this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
            return
          }
          if (status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
        })
    },
    changeSelectedAccount(account) {
      if (account) {
        this.selectedAccount = account
        this.accountId = account?.id || 0
      }
      if (account?.bucketName) {
        const item = { status: null, name: account.bucketName, ...account }
        this.buckets = item
        this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
      } else {
        this.getBuckets()
      }

      if (this.selectedAccount.bucketName) {
        this.defaultBucketName = this.selectedAccount.bucketName

        this.clearAndFetchAnalytics()
      }

      return this.selectedAccount
    },
    clearAndFetchAnalytics() {
      const query = this.generateQuery(this.filterName, [this.defaultBucketName])
      this.$store.commit('storageAnalytics/CLEAR_ANALYTICS')
      this.applyFilters(query)
    },
  },

}
</script>

<style lang="scss" scoped>
.dashboardWrapper {
  margin: auto;
  max-width: 1920px;
  width: 100%;
  height: 100%;
}
.custom-position::v-deep{
  .popup {
    top: 20%;
  }
}

.gap-16{
  gap:16px
}
</style>
